import { useCallback, useEffect, useRef, useState } from "react";
import type React from "react";

export function useMediaPickers() {
  const [gifOpen, setGifOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [composerPickerOpen, setComposerPickerOpen] = useState(false);
  const addBtnRef = useRef<HTMLButtonElement | null>(null);
  const addMenuRef = useRef<HTMLDivElement | null>(null);
  const lastTouchAtRef = useRef<number>(0);

  const handleAddClick = useCallback(() => {
    if (Date.now() - (lastTouchAtRef.current || 0) < 500) return;
    setAddOpen((v) => !v);
  }, []);

  const handleAddTouch = useCallback((e: React.TouchEvent<HTMLButtonElement>) => {
    e.preventDefault();
    lastTouchAtRef.current = Date.now();
    setAddOpen((v) => !v);
  }, []);

  const handleOpenGif = useCallback(() => {
    setGifOpen(true);
    setAddOpen(false);
  }, []);

  const addRecentEmoji = useCallback((ch: string) => {
    try {
      if (!ch || ch === "?" || ch === "??") return;
      const raw = localStorage.getItem("emojiRecent");
      const prev = raw ? JSON.parse(raw) : [];
      const arr = [ch, ...prev.filter((c: string) => c !== ch)].slice(0, 24);
      localStorage.setItem("emojiRecent", JSON.stringify(arr));
    } catch {
      /* noop */
    }
  }, []);

  // Close the add menu when clicking or tapping outside, or on Escape
  useEffect(() => {
    if (!addOpen) return;
    const pointFromEvent = (e: Event): { x: number; y: number } | null => {
      if ("clientX" in e && "clientY" in e) {
        return { x: (e as PointerEvent | MouseEvent).clientX, y: (e as PointerEvent | MouseEvent).clientY };
      }
      const touchEvent = e as TouchEvent;
      const touch = touchEvent.touches?.[0] || touchEvent.changedTouches?.[0];
      return touch ? { x: touch.clientX, y: touch.clientY } : null;
    };
    const pointInside = (el: HTMLElement | null, point: { x: number; y: number } | null) => {
      if (!el || !point) return false;
      const rect = el.getBoundingClientRect();
      return point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;
    };
    const onOutside = (e: Event) => {
      const t = e.target as Node | null;
      const point = pointFromEvent(e);
      const insideBtn = addBtnRef.current && t ? addBtnRef.current.contains(t) : false;
      const insideMenu = addMenuRef.current && t ? addMenuRef.current.contains(t) : false;
      const visuallyInsideBtn = pointInside(addBtnRef.current, point);
      const visuallyInsideMenu = pointInside(addMenuRef.current, point);
      if (!insideBtn && !insideMenu && !visuallyInsideBtn && !visuallyInsideMenu) setAddOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setAddOpen(false); };
    const canUsePointer = typeof window !== "undefined" && "PointerEvent" in window;
    if (canUsePointer) {
      document.addEventListener("pointerdown", onOutside, true);
    } else {
      document.addEventListener("mousedown", onOutside, true);
      document.addEventListener("touchstart", onOutside, true);
    }
    document.addEventListener("keydown", onKey);
    return () => {
      if (canUsePointer) {
        document.removeEventListener("pointerdown", onOutside, true);
      } else {
        document.removeEventListener("mousedown", onOutside, true);
        document.removeEventListener("touchstart", onOutside, true);
      }
      document.removeEventListener("keydown", onKey);
    };
  }, [addOpen]);

  return {
    gifOpen,
    setGifOpen,
    addOpen,
    setAddOpen,
    composerPickerOpen,
    setComposerPickerOpen,
    addBtnRef,
    addMenuRef,
    handleAddClick,
    handleAddTouch,
    handleOpenGif,
    addRecentEmoji,
  };
}
